/**
 * Employés (workers) — einheitlicher KV-Schlüssel + Legacy-Wiederherstellung.
 * Verhindert Überschreiben mit [] vor dem Laden und ID-Präfix-Mismatch (business_).
 */

import {
  kvGet,
  kvSet,
  kvRemove,
  kvSetPersisted,
  kvRemovePersisted,
  listKvKeys
} from '../infrastructure/storage/kvAccess';
import { getCurrentBusinessId, normalizeBusinessStorageId } from './sharedCore';
import { normalizeWorkerPermissions } from './workerPermissions';
import {
  readStoredCurrentBusiness,
  readStoredSessionUser
} from '../services/offlineAuthSession';

/** Business-ID für workers_* — explizit oder aus Session/KV. */
export function resolveWorkersBusinessId(businessId) {
  if (businessId != null && businessId !== '') {
    return normalizeBusinessStorageId(businessId);
  }
  return resolveBusinessIdFromKv() || getCurrentBusinessId({ allowFallback: false });
}

function resolveBusinessIdFromKv() {
  try {
    const business = readStoredCurrentBusiness();
    if (business?.id) return normalizeBusinessStorageId(business.id);
    const user = readStoredSessionUser();
    if (user?.business?.id) return normalizeBusinessStorageId(user.business.id);
  } catch {
    /* ignore */
  }
  return null;
}

/** Kanonischer Key: workers_<id ohne business_-Präfix> oder "workers" ohne Business. */
export function workersStorageKey(businessId) {
  if (businessId == null || businessId === '') return 'workers';
  return `workers_${normalizeBusinessStorageId(businessId)}`;
}

/** Alle bekannten Legacy-Keys (Reihenfolge: kanonisch zuerst, dann Varianten). */
export function legacyWorkersStorageKeys(businessId) {
  if (businessId == null || businessId === '') return ['workers'];

  const raw = String(businessId);
  const normalized = normalizeBusinessStorageId(businessId);
  const keys = [];

  const add = (k) => {
    if (k && !keys.includes(k)) keys.push(k);
  };

  add(workersStorageKey(businessId));
  add(`workers_${raw}`);
  if (normalized !== raw) add(`workers_${normalized}`);
  if (!raw.startsWith('business_')) add(`workers_business_${raw}`);
  if (normalized !== raw) add(`workers_business_${normalized}`);
  add('workers');

  return keys;
}

/**
 * @returns {{ workers: Array, corrupted: boolean }}
 */
export function parseWorkersKvValue(value) {
  if (value == null) return { workers: [], corrupted: false };

  if (typeof value === 'string') {
    if (
      value === '[object Object]' ||
      value === '"[object Object]"' ||
      value.trim() === ''
    ) {
      return { workers: [], corrupted: true };
    }
    try {
      const parsed = JSON.parse(value);
      if (!Array.isArray(parsed)) return { workers: [], corrupted: true };
      return { workers: parsed, corrupted: false };
    } catch {
      return { workers: [], corrupted: true };
    }
  }

  if (Array.isArray(value)) return { workers: value, corrupted: false };
  return { workers: [], corrupted: true };
}

/**
 * Lädt Employés; sucht Legacy-Keys und migriert nach kanonischem Key wenn Daten gefunden.
 */
export function loadWorkersForBusiness(businessId) {
  const keys = legacyWorkersStorageKeys(businessId);
  const canonical = workersStorageKey(businessId);
  const collected = [];
  let bestKey = null;
  let bestLen = 0;

  const finalize = (workers, sourceKey, migrated) => {
    const list = Array.isArray(workers) ? workers : [];
    // Always seed-normalize so stored sparse profiles become full explicit sets.
    return {
      workers: list.map((worker) =>
        worker && typeof worker === 'object'
          ? { ...worker, permissions: normalizeWorkerPermissions(worker.permissions) }
          : worker
      ),
      sourceKey,
      migrated,
    };
  };

  for (const key of keys) {
    const raw = kvGet(key);
    if (raw == null) continue;
    const { workers, corrupted } = parseWorkersKvValue(raw);
    if (corrupted) continue;
    if (workers.length > 0) {
      collected.push(workers);
      if (workers.length >= bestLen) {
        bestLen = workers.length;
        bestKey = key;
      }
    }
  }

  if (collected.length > 0) {
    const merged = mergeWorkersLists(...collected);
    if (merged.length > 0) {
      if (bestKey !== canonical || merged.length > bestLen) {
        kvSet(canonical, merged);
        if (merged.length > bestLen) {
          console.info(
            `[workersStorage] Merged ${merged.length} worker(s) from ${collected.length} key(s) → ${canonical}`
          );
        } else if (bestKey !== canonical) {
          console.info(
            `[workersStorage] Migrated ${merged.length} worker(s) from ${bestKey} → ${canonical}`
          );
        }
      }
      return finalize(merged, bestKey, bestKey !== canonical || merged.length > bestLen);
    }
  }

  const canonicalRaw = kvGet(canonical);
  if (canonicalRaw != null) {
    const { workers, corrupted } = parseWorkersKvValue(canonicalRaw);
    if (!corrupted && Array.isArray(workers)) {
      return finalize(workers, canonical, false);
    }
  }

  return { workers: [], sourceKey: null, migrated: false };
}

/**
 * Speichert nur wenn erlaubt — blockiert []-Überschreiben wenn noch Daten unter Legacy-Keys liegen.
 */
export async function persistWorkersForBusiness(businessId, workers, options = {}) {
  const { allowEmpty = false, skipCloudSync = false } = options;
  const rawList = Array.isArray(workers) ? workers : [];
  // Persist only canonical permissions (boolean coerce + stock/void rules).
  const list = rawList.map((worker) =>
    worker && typeof worker === 'object'
      ? {
          ...worker,
          permissions: normalizeWorkerPermissions(worker.permissions),
        }
      : worker
  );
  const resolvedId = resolveWorkersBusinessId(businessId);
  const key = workersStorageKey(resolvedId);

  if (!allowEmpty && list.length === 0) {
    const existing = loadWorkersForBusiness(resolvedId);
    if (existing.workers.length > 0) {
      return { skipped: true, reason: 'would_overwrite_non_empty', key };
    }
  }

  if (!resolvedId && list.length > 0) {
    console.warn('[workersStorage] Cannot persist workers — no business id');
    return { skipped: true, reason: 'missing_business_id', key };
  }

  await kvSetPersisted(key, list);

  if (resolvedId && list.length > 0) {
    await consolidateLegacyWorkersKeys(resolvedId, list);
  }

  try {
    const { scheduleSequenceSyncAfterWorkersWrite } = await import(
      '../infrastructure/storage/sequenceSyncAfterWrite'
    );
    scheduleSequenceSyncAfterWorkersWrite(resolvedId);
  } catch {
    /* non-blocking */
  }

  if (!skipCloudSync && resolvedId && list.length > 0) {
    try {
      const { enqueueParallelCloudFieldSync } = await import(
        '../services/parallelCloudFieldSync'
      );
      void enqueueParallelCloudFieldSync(resolvedId, 'workers', list).catch(() => {});
    } catch {
      /* cloud optional */
    }
  }

  return { skipped: false, key, businessId: resolvedId };
}

/**
 * Schreibt kanonischen Key und entfernt Legacy-Varianten (workers_business_*, bare workers, …).
 */
export async function consolidateLegacyWorkersKeys(businessId, workers) {
  const resolvedId = resolveWorkersBusinessId(businessId);
  const list = Array.isArray(workers) ? workers : [];
  const canonical = workersStorageKey(resolvedId);

  if (list.length > 0) {
    await kvSetPersisted(canonical, list);
  }

  for (const legacyKey of legacyWorkersStorageKeys(resolvedId)) {
    if (legacyKey === canonical) continue;
    kvRemove(legacyKey);
    await kvRemovePersisted(legacyKey);
  }
}

export function removeAllWorkersKeysForBusiness(businessId) {
  legacyWorkersStorageKeys(businessId).forEach((k) => kvRemove(k));
}

/**
 * Beim App-Start: Legacy-Keys einlesen und in kanonischen Key migrieren (ohne UI).
 */
export function recoverWorkersOnStartup() {
  try {
    const fromSession = loadWorkersForBusiness(resolveBusinessIdFromKv());
    if (fromSession.workers.length > 0) {
      return fromSession;
    }

    let best = null;
    let bestKey = null;
    for (const key of listKvKeys().filter((k) => k === 'workers' || k.startsWith('workers_'))) {
      const suffix = key === 'workers' ? null : key.slice('workers_'.length);
      const { workers, corrupted } = parseWorkersKvValue(kvGet(key));
      if (corrupted || !workers.length) continue;
      if (!best || workers.length >= best.length) {
        best = workers;
        bestKey = key;
        const canonical = workersStorageKey(suffix);
        if (canonical !== key && suffix != null) {
          kvSet(canonical, workers);
        }
      }
    }
    if (best?.length) {
      return { workers: best, sourceKey: bestKey, migrated: true };
    }
    return { workers: [], sourceKey: null, migrated: false };
  } catch (e) {
    console.warn('[workersStorage] recoverWorkersOnStartup failed:', e?.message || e);
    return { workers: [], sourceKey: null, migrated: false };
  }
}

export function migrateWorkersExpensesPermission(workers) {
  if (!Array.isArray(workers)) return [];
  return workers.map((worker) => ({
    ...worker,
    permissions: normalizeWorkerPermissions(worker.permissions)
  }));
}

function repairWorkerId(existingIds) {
  let candidate;
  let attempts = 0;
  do {
    candidate = `TRAV-${new Date().getFullYear()}-${Date.now().toString().slice(-6)}${attempts}`;
    attempts += 1;
  } while (existingIds.has(candidate) && attempts < 50);
  return candidate;
}

/**
 * Fusionne plusieurs listes sans perdre d'employés (clé: code, puis id, puis nom).
 * Répare les id dupliqués au lieu de supprimer le travailleur.
 * Permissions: deep-merge + normalize (évite sparse cloud → re-DEFAULT des modules).
 */
export function mergeWorkersLists(...lists) {
  const byIdentity = new Map();

  for (const list of lists) {
    if (!Array.isArray(list)) continue;
    for (const worker of list) {
      if (!worker || typeof worker !== 'object') continue;
      const codeKey = worker.code != null ? String(worker.code).trim() : '';
      const idKey = worker.id != null ? String(worker.id).trim() : '';
      const nameKey = worker.name != null ? String(worker.name).trim().toLowerCase() : '';
      const identityKey = codeKey
        ? `code:${codeKey}`
        : idKey
          ? `id:${idKey}`
          : nameKey
            ? `name:${nameKey}`
            : `row:${byIdentity.size}`;

      const existing = byIdentity.get(identityKey);
      if (!existing) {
        byIdentity.set(identityKey, {
          ...worker,
          permissions: normalizeWorkerPermissions(worker.permissions),
        });
        continue;
      }

      // Later list wins on scalar fields; permissions merge deep then normalize
      // so a sparse remote object does not re-open modules that are still false locally.
      byIdentity.set(identityKey, {
        ...existing,
        ...worker,
        permissions: normalizeWorkerPermissions({
          ...(existing.permissions && typeof existing.permissions === 'object'
            ? existing.permissions
            : {}),
          ...(worker.permissions && typeof worker.permissions === 'object'
            ? worker.permissions
            : {}),
        }),
      });
    }
  }

  const seenIds = new Set();
  return Array.from(byIdentity.values()).map((worker) => {
    let id = worker.id != null ? String(worker.id).trim() : '';
    if (!id || seenIds.has(id)) {
      id = repairWorkerId(seenIds);
    }
    seenIds.add(id);
    return {
      ...worker,
      id,
      permissions: normalizeWorkerPermissions(worker.permissions),
    };
  });
}
